import React, { useState, useEffect, useCallback } from 'react';
import { Phone, Calendar, Clock, User, MapPin, RefreshCw, CheckCircle, XCircle, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { bookingRequestAPI } from '../../../api/bookingRequest';
import { toast } from 'react-toastify';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  contacted: 'bg-blue-100 text-blue-800 border-blue-200',
  confirmed: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200',
  completed: 'bg-gray-100 text-gray-800 border-gray-200',
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', icon: Clock },
  { value: 'contacted', label: 'Contacted', icon: Phone },
  { value: 'confirmed', label: 'Confirmed', icon: CheckCircle },
  { value: 'cancelled', label: 'Cancelled', icon: XCircle },
];

const TIME_LABELS = {
  morning: 'Morning (9 AM - 12 PM)',
  afternoon: 'Afternoon (12 PM - 4 PM)',
  evening: 'Evening (4 PM - 7 PM)',
};

const BookingRequests = ({ onConvertToAppointment }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [expandedId, setExpandedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await bookingRequestAPI.list({ 
        status: filter === 'all' ? undefined : filter,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });
      if (response.success) {
        setRequests(response.data || []);
      } else {
        toast.error(response.message || 'Failed to load booking requests');
      }
    } catch (error) {
      console.error('Error loading booking requests:', error);
      toast.error('Failed to load booking requests');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleStatusUpdate = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      const response = await bookingRequestAPI.updateStatus(id, newStatus);
      if (response.success) {
        toast.success(`Status updated to ${newStatus}`);
        loadRequests();
      } else {
        toast.error(response.message || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', { 
        weekday: 'short', 
        day: 'numeric', 
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatCreatedAt = (dateStr) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      
      return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    } catch {
      return '';
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="h-full flex flex-col" style={{ fontFamily: 'Poppins' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-black">
            Website Booking Requests
          </h1>
          <p className="text-lg text-gray-600 mt-1 font-medium">
            Manage appointment requests from the public booking form
          </p>
        </div>
        <button
          onClick={loadRequests}
          disabled={loading}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#2D869C] text-white rounded-xl hover:bg-[#2D869C]/90 transition-colors disabled:opacity-50 shadow-sm text-lg font-medium"
        >
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center bg-gray-100 rounded-full p-1 mb-6 flex-wrap gap-1">
        <button
          onClick={() => setFilter('all')}
          className={`px-5 py-2.5 rounded-full text-lg font-medium transition-all duration-200 ${
            filter === 'all' 
              ? 'bg-[#2D869C] text-white shadow-sm' 
              : 'text-gray-600 hover:text-[#2D869C] hover:bg-gray-200'
          }`}
        >
          All
        </button>
        {STATUS_OPTIONS.map((status) => (
          <button
            key={status.value}
            onClick={() => setFilter(status.value)}
            className={`px-5 py-2.5 rounded-full text-lg font-medium transition-all duration-200 flex items-center gap-2 whitespace-nowrap ${
              filter === status.value 
                ? 'bg-[#2D869C] text-white shadow-sm' 
                : 'text-gray-600 hover:text-[#2D869C] hover:bg-gray-200'
            }`}
          >
            <status.icon size={18} />
            {status.label}
          </button>
        ))}
      </div>

      {/* Requests List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-border"></div>
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No booking requests</h3>
            <p className="text-lg text-gray-500 font-medium">
              {filter === 'pending' 
                ? 'No pending requests at the moment' 
                : `No ${filter} requests found`}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map((request) => (
              <div
                key={request._id}
                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Main Row */}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    {/* Patient Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 rounded-full bg-[#9DC9D4]/30 flex items-center justify-center border-2 border-[#2D869C]">
                          <User size={20} className="text-[#2D869C]" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-black">
                            {request.patient?.firstName || request.firstName} {request.patient?.lastName || request.lastName}
                          </h3>
                          <div className="flex items-center gap-2 text-lg text-gray-600 font-medium">
                            <Phone size={16} />
                            <a 
                              href={`tel:${request.patient?.mobile || request.mobile}`}
                              className="text-[#2D869C] hover:underline font-medium"
                            >
                              {request.patient?.mobile || request.mobile}
                            </a>
                          </div>
                        </div>
                      </div>

                      {/* Appointment Details */}
                      <div className="flex flex-wrap gap-5 text-lg text-gray-600 font-medium mt-3">
                        <div className="flex items-center gap-2">
                          <Calendar size={18} className="text-gray-500" />
                          <span>{formatDate(request.preferredDate)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock size={18} className="text-gray-500" />
                          <span>{TIME_LABELS[request.preferredTime] || request.preferredTime}</span>
                        </div>
                        {request.department && (
                          <div className="flex items-center gap-2">
                            <MapPin size={18} className="text-gray-500" />
                            <span className="capitalize">{request.department.replace('-', ' ')}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status & Actions */}
                    <div className="flex flex-col items-end gap-2">
                      <span className={`px-4 py-1.5 rounded-full text-md font-semibold border capitalize ${STATUS_COLORS[request.status] || STATUS_COLORS.pending}`}>
                        {request.status}
                      </span>
                      <span className="text-md text-gray-500 font-medium">
                        {formatCreatedAt(request.createdAt)}
                      </span>
                    </div>
                  </div>

                  {/* Reference Number & Expand Button */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                    <span className="text-md text-gray-500 font-medium">
                      Ref: <span className="font-mono font-semibold text-gray-700">{request.referenceNumber}</span>
                    </span>
                    <button
                      onClick={() => toggleExpand(request._id)}
                      className="flex items-center gap-1.5 text-lg text-[#2D869C] hover:text-[#2D869C]/80 font-medium transition-colors"
                    >
                      {expandedId === request._id ? (
                        <>Hide Actions <ChevronUp size={20} /></>
                      ) : (
                        <>Show Actions <ChevronDown size={20} /></>
                      )}
                    </button>
                  </div>
                </div>

                {/* Expanded Actions */}
                {expandedId === request._id && (
                  <div className="px-5 pb-5 pt-3 bg-gray-50 border-t border-gray-200">
                    {request.reason && (
                      <div className="mb-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                        <p className="text-md text-gray-500 mb-2 font-medium">Reason for Visit:</p>
                        <p className="text-lg text-gray-700 font-medium">{request.reason}</p>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-3">
                      {/* Quick Call Button */}
                      <a
                        href={`tel:${request.patient?.mobile || request.mobile}`}
                        className="flex items-center gap-2 px-5 py-2.5 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors shadow-sm text-lg font-medium"
                      >
                        <Phone size={20} />
                        Call Patient
                      </a>

                      {/* Status Update Buttons */}
                      {request.status === 'pending' && (
                        <button
                          onClick={() => handleStatusUpdate(request._id, 'contacted')}
                          disabled={updatingId === request._id}
                          className="flex items-center gap-2 px-5 py-2.5 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50 shadow-sm text-lg font-medium"
                        >
                          <Phone size={20} />
                          Mark as Contacted
                        </button>
                      )}

                      {(request.status === 'pending' || request.status === 'contacted') && (
                        <>
                          <button
                            onClick={() => handleStatusUpdate(request._id, 'confirmed')}
                            disabled={updatingId === request._id}
                            className="flex items-center gap-2 px-5 py-2.5 bg-theme-success text-white rounded-xl hover:opacity-90 transition-colors disabled:opacity-50 shadow-sm text-lg font-medium"
                          >
                            <CheckCircle size={20} />
                            Confirm
                          </button>
                          <button
                            onClick={() => handleStatusUpdate(request._id, 'cancelled')}
                            disabled={updatingId === request._id}
                            className="flex items-center gap-2 px-5 py-2.5 bg-theme-error text-white rounded-xl hover:opacity-90 transition-colors disabled:opacity-50 shadow-sm text-lg font-medium"
                          >
                            <XCircle size={20} />
                            Cancel
                          </button>
                        </>
                      )}

                      {/* Convert to Appointment */}
                      {request.status !== 'cancelled' && request.status !== 'completed' && onConvertToAppointment && (
                        <button
                          onClick={() => onConvertToAppointment(request)}
                          className="flex items-center gap-2 px-5 py-2.5 bg-[#2D869C] text-white rounded-xl hover:bg-[#2D869C]/90 transition-colors shadow-sm text-lg font-medium"
                        >
                          <Calendar size={20} />
                          Book Appointment
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingRequests;
